/**
 * AJ Lead Capture — frontend runtime entry point.
 *
 * Loaded with `defer`; bootstraps after `window.load`. Fetches the offer
 * manifest, matches the current URL, evaluates suppression rules, arms the
 * trigger for the most-specific matching offer, renders the surface, and wires
 * conversions + analytics. Also hydrates inline shortcode placeholders and
 * supports admin preview mode (?aj_preview=<id>).
 *
 * @module runtime
 */

import { getManifest, postEvent, postConvert } from './api';
import * as store from './storage';
import { arm } from './triggers';
import { ga } from './analytics';
import { openModal } from './modal';
import { openToast } from './toast';
import { renderInline } from './inline';

const cfg = window.AJ_LO || {};
let cssInjected = false;
// Page-scoped guard: at most one POPUP (modal/toast) is shown at a time.
// Inline offers are independent and do not set or honor this flag.
let popupShown = false;

/**
 * Handle a ?aj_reset=<id|all> request: clear stored state so offers re-fire.
 * Lets an admin (or anyone testing) un-cookie themselves.
 *
 * @returns {void}
 */
function applyReset() {
  const reset = new URLSearchParams(window.location.search).get('aj_reset');
  if (!reset) {
    return;
  }
  if (reset === 'all') {
    store.clearNamespace('aj_lo_');
    return;
  }
  const id = parseInt(reset, 10);
  if (id) {
    store.remove(`aj_lo_converted_${id}`);
    store.remove(`aj_lo_dismissed_${id}`);
    store.sessionRemove(`aj_lo_shown_${id}`);
  }
  store.remove('aj_lo_global_convert');
}

/**
 * Lazily inject the stylesheet (kept off the critical path).
 *
 * @returns {void}
 */
function injectCss() {
  if (cssInjected || !cfg.css) {
    return;
  }
  cssInjected = true;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = cfg.css;
  document.head.appendChild(link);
}

/**
 * Convert a glob pattern to a RegExp anchored to the full path.
 *
 * @param {string} pattern Glob (supports *).
 * @returns {RegExp} Compiled matcher.
 */
function globToRegExp(pattern) {
  const escaped = pattern.replace(/[.+^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*');
  return new RegExp(`^${escaped}$`);
}

/**
 * Whether a path matches the offer's patterns. A pattern starting with "!"
 * excludes: the path must match a plain pattern and no "!" pattern.
 *
 * @param {string}   path     Current pathname.
 * @param {string[]} patterns URL glob patterns.
 * @returns {boolean} Match.
 */
function matchesUrl(path, patterns) {
  const list = patterns || [];
  const excluded = list.some((p) => p.startsWith('!') && globToRegExp(p.slice(1)).test(path));
  return !excluded && list.some((p) => !p.startsWith('!') && globToRegExp(p).test(path));
}

/**
 * Specificity score for an offer's matching patterns (more literal = higher).
 *
 * @param {string}   path  Current pathname.
 * @param {string[]} patterns URL patterns.
 * @returns {number} Score.
 */
function specificity(path, patterns) {
  let best = 0;
  (patterns || []).forEach((p) => {
    if (!p.startsWith('!') && globToRegExp(p).test(path)) {
      const literal = p.replace(/\*/g, '').length;
      best = Math.max(best, literal);
    }
  });
  return best;
}

/**
 * Suppression decision for an auto-surfaced offer.
 *
 * @param {Object} offer Manifest offer.
 * @returns {boolean} Whether the offer may show.
 */
function canShow(offer) {
  // A conversion on ANY offer stops all campaigns.
  if (store.get('aj_lo_global_convert')) {
    return false;
  }
  // Exclusive offers yield to any popup that already appeared this session.
  if (offer.exclusive && store.sessionHas('aj_lo_popup_seen')) {
    return false;
  }
  if (store.get(`aj_lo_converted_${offer.id}`)) {
    return false;
  }
  if (store.get(`aj_lo_dismissed_${offer.id}`)) {
    return false;
  }
  if (store.sessionHas(`aj_lo_shown_${offer.id}`)) {
    return false;
  }
  return true;
}

/**
 * Common event payload for an offer.
 *
 * @param {Object} offer   Offer.
 * @param {string} surface Surface label.
 * @returns {Object} Payload.
 */
function eventBase(offer, surface) {
  return {
    offer_id: offer.id,
    offer_title: (offer.copy && offer.copy.headline) || '',
    surface,
    trigger: offer.trigger,
    page_path: window.location.pathname,
  };
}

/**
 * Build the submit handler that posts a conversion.
 *
 * @param {Object} offer   Offer.
 * @param {string} surface Surface label.
 * @returns {Function} onSubmit({email, name}) -> Promise<{message}>.
 */
function makeSubmit(offer, surface) {
  return ({ email, name }) => postConvert({
    offer_id: offer.id,
    email,
    name,
    surface,
    url: window.location.pathname,
    email_confirm: '',
  }).then((res) => {
    store.set(`aj_lo_converted_${offer.id}`, Date.now(), offer.conversion_cookie_days || 365);
    // Stop all campaigns after any conversion (long-lived).
    store.set('aj_lo_global_convert', Date.now(), 365);
    ga('lead_offer_convert', eventBase(offer, surface));
    if (res && res.redirect) {
      window.setTimeout(() => { window.location.href = res.redirect; }, 900);
    }
    return { message: res && res.message };
  });
}

/**
 * Show an auto surface (modal or toast).
 *
 * @param {Object}  offer   Offer.
 * @param {boolean} preview Preview mode (skip state writes/logging).
 * @returns {void}
 */
function showSurface(offer, preview) {
  // Never show two popups at once — first to fire wins, the rest yield.
  if (!preview && popupShown) {
    return;
  }
  // Another aj-* plugin's modal is open (e.g. the aj-events RSVP flyout):
  // don't stack a popup on top of it.
  if (!preview && document.body.classList.contains('aj-modal-open')) {
    return;
  }

  injectCss();
  const surface = offer.surface === 'toast' ? 'toast' : 'modal';

  if (!preview) {
    popupShown = true;
    store.sessionSet(`aj_lo_shown_${offer.id}`);
    // Session-wide "a popup has appeared" marker; exclusive offers honor it.
    store.sessionSet('aj_lo_popup_seen');
    postEvent({
      offer_id: offer.id, event_type: 'impression', surface, url: window.location.pathname,
    });
    ga('lead_offer_impression', eventBase(offer, surface));
  }

  const previewSubmit = () => Promise.resolve({
    message: offer.copy && offer.copy.success_message,
  });

  const handlers = {
    onSubmit: preview ? previewSubmit : makeSubmit(offer, surface),
    onClose: () => {
      if (preview) {
        return;
      }
      store.set(`aj_lo_dismissed_${offer.id}`, Date.now(), offer.frequency_days || 30);
      postEvent({
        offer_id: offer.id, event_type: 'dismiss', surface, url: window.location.pathname,
      });
      ga('lead_offer_dismiss', eventBase(offer, surface));
    },
  };

  if (surface === 'toast') {
    openToast(offer, handlers);
  } else {
    openModal(offer, handlers);
  }
}

/**
 * Hydrate inline shortcode placeholders (data-aj-offer="<id>").
 *
 * @param {Object[]} offers Manifest offers.
 * @returns {void}
 */
function hydrateInline(offers) {
  // A conversion on any offer stops all campaigns, inline included.
  if (store.get('aj_lo_global_convert')) {
    return;
  }

  const slots = document.querySelectorAll('[data-aj-offer]');
  slots.forEach((slot) => {
    const id = parseInt(slot.getAttribute('data-aj-offer'), 10);
    const base = offers.find((o) => o.id === id);
    if (!base) {
      return;
    }

    // Merge per-page copy overrides from data-aj-* attributes.
    const overrideKeys = ['headline', 'subhead', 'body', 'cta-label', 'success-message'];
    const overrides = {};
    overrideKeys.forEach((k) => {
      const v = slot.getAttribute(`data-aj-${k}`);
      if (v != null) {
        overrides[k.replace('-', '_')] = v;
      }
    });
    const offer = { ...base, copy: { ...base.copy, ...overrides } };

    injectCss();
    renderInline(slot, offer, { onSubmit: makeSubmit(offer, 'inline') });
    // Inline coexists with popups — it does not occupy the popup slot.
    postEvent({
      offer_id: id, event_type: 'impression', surface: 'inline', url: window.location.pathname,
    });
    ga('lead_offer_impression', eventBase(offer, 'inline'));
  });
}

/**
 * Initialize the runtime.
 *
 * @returns {void}
 */
async function init() {
  if (!cfg.rest) {
    return;
  }
  applyReset();
  const manifest = await getManifest();
  if (!manifest || !Array.isArray(manifest.offers)) {
    return;
  }
  const { offers } = manifest;

  // Inline shortcodes render regardless of URL targeting.
  hydrateInline(offers);

  // Preview mode: force-show a specific offer for admins.
  const previewId = parseInt(cfg.preview, 10) || 0;
  if (previewId) {
    const offer = offers.find((o) => o.id === previewId);
    if (offer && offer.surface !== 'inline_only') {
      showSurface(offer, true);
    }
    return;
  }

  // Auto surfaces (modal/toast): all matches arm; the first trigger to fire
  // shows its offer and the rest yield (never two popups at once). Priority
  // then specificity orders simultaneous ties.
  const path = window.location.pathname;
  const matches = offers
    .filter((o) => o.surface !== 'inline_only' && matchesUrl(path, o.url_patterns) && canShow(o))
    .sort((a, b) => (a.priority - b.priority)
      || (specificity(path, b.url_patterns) - specificity(path, a.url_patterns)));

  matches.forEach((offer) => {
    arm(offer.trigger, offer.trigger_value, () => showSurface(offer, false));
  });
}

if (document.readyState === 'complete') {
  init();
} else {
  window.addEventListener('load', init, { once: true });
}
